import React, { useState, useEffect, useCallback } from 'react';
import { warehouseService } from '../../services/warehouseService';
import { useLocation } from '../../contexts/LocationContext';
import api from '../../services/api';
import { validators } from '../../utils/validation';

// ---- Shared sub-components ----

/** Error / success alert banner */
export const AlertBanner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      padding: '0.35rem 0.5rem',
      marginBottom: '0.5rem',
      backgroundColor: isError ? 'var(--danger-light)' : 'var(--success-light)',
      color: isError ? 'var(--danger)' : 'var(--success)',
      borderRadius: '4px',
      fontSize: '11px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      {message}
      <button
        style={{  background: 'none', color: isError ? 'var(--danger)' : 'var(--success)', cursor: 'pointer', padding: '0' }}
        onClick={onClose}
      >{'×'}</button>
    </div>
  );
};

/** Two-tab switcher (new / history) */
export const TabSwitcher = ({ activeTab, onTabChange, tabs }) => (
  <div style={{
    display: 'flex',
    gap: '0.25rem',
    marginBottom: '0.5rem',
    padding: '0.35rem',
    backgroundColor: 'var(--card-bg)',
    borderRadius: '4px',
    border: '1px solid var(--border)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  }}>
    {tabs.map(tab => (
      <button
        key={tab.key}
        style={{
          padding: '4px 10px',
          fontSize: '11px',
          fontWeight: activeTab === tab.key ? '600' : '500',
          border: activeTab === tab.key ? `1px solid ${tab.activeColor}` : '1px solid var(--border)',
          backgroundColor: activeTab === tab.key ? tab.activeColor : 'var(--card-bg)' ,
          color: activeTab === tab.key ? '#fff' : 'var(--text-secondary)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          transition: 'all 0.15s ease-in-out'
        }}
        onClick={() => onTabChange(tab.key)}
      >
        {tab.icon && <i className={tab.icon} style={{ fontSize: '10px' }}></i>}
        {tab.iconEmoji && <span>{tab.iconEmoji}</span>}
        {tab.label}
      </button>
    ))}
  </div>
);

/** Product selection modal with warehouse selector and search */
export const ProductSelectionModal = ({
  show,
  onClose,
  products,
  loadingProducts,
  searchTerm,
  onSearchChange,
  selectedProducts,
  onAddProduct,
  warehouses,
  selectedWarehouse,
  onWarehouseChange,
  gradient = 'linear-gradient(135deg, var(--orange), var(--danger))',
  spinnerColor = 'var(--orange)',
  addButtonColor = 'var(--orange)',
  addButtonHoverColor = 'var(--danger)',
  icon = '',
  title = 'Wybierz produkt',
  subtitle = '',
  hintBg = 'var(--warning-light)',
  hintColor = 'var(--warning)',
  hintIcon = '',
  hintText = '',
  showStock = true,
  warehouseLabel = 'Magazyn',
}) => {
  if (!show) return null;
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: "var(--card-bg)",
        borderRadius: '6px',
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Header */}
        <div style={{
          padding: '0.75rem 1rem',
          background: gradient,
          color: '#fff' ,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <div style={{
            width: '2rem',
            height: '2rem',
            background: 'var(--bg-secondary)',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1rem'
          }}>
            {icon}
          </div>
          <div style={{ flex: 1 }}>
            <h5 style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>
              {title}
            </h5>
            {subtitle && (
              <p style={{ margin: 0, fontSize: '11px', opacity: 0.9 }}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              
              fontSize: '1.25rem',
              color: '#fff' ,
              cursor: 'pointer',
              padding: '0.25rem',
              lineHeight: 1
            }}
          >
            {'✕'}
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1rem', maxHeight: 'calc(90vh - 60px)', overflowY: 'auto' }}>
          {/* Warehouse selector */}
          {warehouses && (
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '500',
                color: 'var(--text-muted)',
                marginBottom: '3px'
              }}>
                {warehouseLabel}
              </label>
              <select
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text)'
                }}
                value={selectedWarehouse || ''}
                onChange={(e) => onWarehouseChange(parseInt(e.target.value))}
              >
                {warehouses.length === 0 ? (
                  <option value="">Brak magazyn{'ó'}w dla tej lokalizacji</option>
                ) : (
                  warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name || w.nazwa}</option>
                  ))
                )}
              </select>
            </div>
          )}

          {/* Search */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: '500',
              color: 'var(--text-muted)',
              marginBottom: '3px'
            }}>
              Szukaj produkt{'ó'}w
            </label>
            <input
              type="text"
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '12px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)'
              }}
              placeholder="Wpisz min. 2 znaki aby wyszukać..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          {/* Product table */}
          {loadingProducts ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid var(--border)',
                borderTop: `4px solid ${spinnerColor}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
              <p style={{ marginTop: '1rem', color: 'var(--text-muted)', fontSize: '12px' }}>{'Ł'}adowanie produkt{'ó'}w...</p>
            </div>
          ) : (
            <div style={{
              border: '1px solid var(--border)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'var(--bg)',
                    borderBottom: '2px solid var(--border)'
                  }}>
                    <tr>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)' }}>Nazwa</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)', width: '110px' }}>Kod</th>
                      {showStock && (
                        <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)', width: '90px' }}>Stan</th>
                      )}
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)', width: '90px' }}>Akcja</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.length === 0 ? (
                      <tr>
                        <td colSpan={showStock ? "4" : "3"} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{'📦'}</div>
                          Brak dost{'ę'}pnych produkt{'ó'}w
                        </td>
                      </tr>
                    ) : (
                      products.map((product) => {
                        const isAdded = selectedProducts.some(p => p.id === product.id);
                        return (
                          <tr key={product.id} style={{
                            borderBottom: '1px solid var(--bg)',
                            transition: 'background-color 0.15s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <td style={{ padding: '10px 12px' }}>
                              <div style={{ fontWeight: '500', color: 'var(--text)', fontSize: '13px' }}>{product.name}</div>
                              {(product.description || product.category) && (
                                <small style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{product.description || product.category}</small>
                              )}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              <code style={{
                                backgroundColor: 'var(--border)',
                                padding: '3px 6px',
                                borderRadius: '3px',
                                fontSize: '11px'
                              }}>
                                {product.barcode || product.product_code || '-'}
                              </code>
                            </td>
                            {showStock && (
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor: (product.stock_quantity || product.quantity || 0) > 0 ? 'var(--success-light)' : 'var(--warning-light)',
                                  color: (product.stock_quantity || product.quantity || 0) > 0 ? 'var(--success)' : 'var(--warning)'
                                }}>
                                  {product.stock_quantity || product.quantity || 0} {product.unit || 'szt'}
                                </span>
                              </td>
                            )}
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <button
                                style={{
                                  padding: '4px 10px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor: isAdded ? 'var(--text-muted)' : addButtonColor,
                                  color: '#fff' ,
                                  cursor: isAdded ? 'default' : 'pointer',
                                  transition: 'background-color 0.15s'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isAdded) e.target.style.backgroundColor = addButtonHoverColor;
                                }}
                                onMouseLeave={(e) => {
                                  if (!isAdded) e.target.style.backgroundColor = addButtonColor;
                                }}
                                onClick={() => onAddProduct(product)}
                                disabled={isAdded}
                              >
                                {isAdded ? '✓ Dodano' : '+ Dodaj'}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Hint */}
          {hintText && (
            <div style={{
              marginTop: '0.75rem',
              padding: '10px 12px',
              backgroundColor: hintBg,
              borderRadius: '4px',
              fontSize: '11px',
              color: hintColor,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>{hintIcon}</span>
              <span>{hintText}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** History table with date/status filters */
export const HistoryPanel = ({
  items,
  columns,
  dateFilter,
  onDateFilterChange,
  statusFilter,
  onStatusFilterChange,
  onRefresh,
  emptyMessage = 'Brak dokumentów',
  emptyIcon = 'fas fa-history',
  title = 'Historia',
  statusOptions = [
    { value: 'all', label: 'Wszystkie' },
    { value: 'completed', label: 'Zatwierdzone' },
    { value: 'pending', label: 'Oczekujące' },
  ],
  renderRow,
}) => (
  <div style={{
    backgroundColor: 'var(--card-bg)',
    border: '1px solid var(--border)',
    borderRadius: '4px'
  }}>
    <div style={{
      padding: '0.4rem 0.6rem',
      backgroundColor: 'var(--bg)',
      borderBottom: '1px solid var(--border)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <span style={{ fontWeight: '600', fontSize: '11px' }}>{title}</span>
      <div style={{ display: 'flex', gap: '0.25rem' }}>
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => onDateFilterChange(e.target.value)}
          style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', width: '130px' }}
        />
        <select
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
          style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', width: '110px' }}
        >
          {statusOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <button
          onClick={onRefresh}
          style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--info)', backgroundColor: 'var(--card-bg)', color: 'var(--info)', cursor: 'pointer' }}
        >
          <i className="fas fa-sync-alt"></i>
        </button>
      </div>
    </div>
    <div style={{ padding: '0.5rem' }}>
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontSize: '11px' }}>
          <i className={emptyIcon} style={{ fontSize: '16px', marginBottom: '0.25rem', display: 'block' }}></i>
          {emptyMessage}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg)' }}>
                {columns.map((col, i) => (
                  <th key={i} style={{
                    padding: '0.3rem 0.5rem',
                    borderBottom: '1px solid var(--border)',
                    textAlign: col.align || 'left',
                    ...(col.width ? { width: col.width } : {})
                  }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => renderRow(item, idx))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

/** Selected products table for new document form */
export const SelectedProductsTable = ({
  products,
  onQuantityChange,
  onReasonChange,
  onRemove,
  onSubmit,
  loading,
  tableTitle = 'Produkty',
  submitLabel = 'Zatwierdź',
  submitIcon = 'fas fa-check',
  submitColor = 'var(--success)',
  showStock = true,
  showReason = true,
  reasonPlaceholder = 'Powód...',
  reasonRequired = false,
  extraColumns,
  renderExtraInputs,
  documentDate,
  onDocumentDateChange,
}) => {
  const [quantityErrors, setQuantityErrors] = useState({});

  const handleQuantityChange = (productId, value) => {
    setQuantityErrors(prev => { const n = {...prev}; delete n[productId]; return n; });
    onQuantityChange(productId, value);
  };

  const handleSubmit = () => {
    const errors = {};
    products.forEach(p => {
      const err = validators.positiveNumber(p.quantity, p.name || 'Ilość');
      if (err) errors[p.id] = `Ilość musi być > 0 (aktualna: ${p.quantity})`;
    });
    if (Object.keys(errors).length > 0) {
      setQuantityErrors(errors);
      return;
    }
    setQuantityErrors({});
    onSubmit();
  };

  return (
  <div style={{
    backgroundColor: 'var(--card-bg)',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    marginBottom: '0.5rem'
  }}>
    <div style={{
      padding: '0.4rem 0.6rem',
      backgroundColor: 'var(--bg)',
      borderBottom: '1px solid var(--border)',
      fontWeight: '600',
      fontSize: '11px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{tableTitle}</span>
        {onDocumentDateChange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '400' }}>Data dokumentu:</label>
            <input
              type="date"
              value={documentDate || ''}
              onChange={(e) => onDocumentDateChange(e.target.value)}
              style={{
                padding: '0.2rem 0.4rem', fontSize: '11px', border: '1px solid var(--border)',
                borderRadius: '3px', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)',
                fontWeight: '400'
              }}
            />
          </div>
        )}
      </div>
    </div>
    <div style={{ padding: '0.5rem' }}>
      {products.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontSize: '11px' }}>
          <i className="fas fa-inbox" style={{ fontSize: '16px', marginBottom: '0.25rem', display: 'block' }}></i>
          Brak wybranych produkt{'ó'}w
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg)' }}>
                <th style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>Produkt</th>
                <th style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>Kod</th>
                {showStock && (
                  <th style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>Stan</th>
                )}
                <th style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'left', width: '80px' }}>Ilo{'ść'}</th>
                {showReason && (
                  <th style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>Pow{'ó'}d</th>
                )}
                {extraColumns && extraColumns.map((col, i) => (
                  <th key={i} style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: col.align || 'left', ...(col.width ? { width: col.width } : {}) }}>
                    {col.label}
                  </th>
                ))}
                <th style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'center', width: '50px' }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                    <strong>{product.name}</strong>
                    {product.description && (
                      <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '10px' }}>{product.description}</span>
                    )}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                    <code style={{ fontSize: '10px' }}>{product.barcode || product.product_code || 'Brak'}</code>
                  </td>
                  {showStock && (
                    <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                      <span style={{
                        padding: '0.1rem 0.3rem',
                        fontSize: '10px',
                        backgroundColor: 'var(--cyan)',
                        color: '#000',
                        borderRadius: '3px'
                      }}>
                        {product.maxQuantity} {product.unit || 'szt'}
                      </span>
                    </td>
                  )}
                  <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                    <input
                      type="number"
                      style={{
                        width: '60px', padding: '0.2rem 0.3rem', fontSize: '11px',
                        border: quantityErrors[product.id] ? '1px solid var(--danger)' : '1px solid var(--border)',
                        borderRadius: '3px'
                      }}
                      value={product.quantity}
                      onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                      min="1"
                      max={product.maxQuantity || undefined}
                    />
                    {quantityErrors[product.id] && (
                      <div style={{ fontSize: '9px', color: 'var(--danger)', marginTop: '1px' }}>{quantityErrors[product.id]}</div>
                    )}
                  </td>
                  {showReason && (
                    <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                      <input
                        type="text"
                        style={{ width: '100%', padding: '0.2rem 0.3rem', fontSize: '11px', border: '1px solid var(--border)', borderRadius: '3px' }}
                        placeholder={reasonPlaceholder}
                        value={product.reason || ''}
                        onChange={(e) => onReasonChange(product.id, e.target.value)}
                        required={reasonRequired}
                      />
                    </td>
                  )}
                  {renderExtraInputs && renderExtraInputs(product)}
                  <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                    <button
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        border: '1px solid var(--danger)', color: 'var(--danger)' ,
                        cursor: 'pointer'
                      }}
                      onClick={() => onRemove(product.id)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {products.length > 0 && onSubmit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
          <button
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '600',
              backgroundColor: submitColor,
              color: '#fff' ,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <span>Przetwarzanie...</span>
            ) : (
              <>
                <i className={submitIcon} style={{ fontSize: '10px' }}></i>
                {submitLabel}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  </div>
  );
};

/** Details/preview modal overlay */
export const DetailsModal = ({
  show,
  onClose,
  title,
  headerBg = 'var(--danger)',
  children,
  maxWidth = '600px',
  footerButtons,
}) => {
  if (!show) return null;
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1050,
      padding: '0.5rem'
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)',
        borderRadius: '8px',
        width: '90%',
        maxWidth,
        maxHeight: '80vh',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: headerBg,
          color: '#fff' ,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: '8px 8px 0 0',
          flexShrink: 0
        }}>
          <strong>{title}</strong>
          <button
            onClick={onClose}
            style={{ background: 'none',  color: '#fff' , fontSize: '1.25rem', cursor: 'pointer' }}
          >{'×'}</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
          {children}
        </div>
        {footerButtons && (
          <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexShrink: 0 }}>
            {footerButtons}
          </div>
        )}
      </div>
    </div>
  );
};

/** Status badge */
export const StatusBadge = ({ status, completedLabel = 'Zatwierdzony', pendingLabel = 'Oczekujący' }) => (
  <span style={{
    padding: '0.1rem 0.3rem',
    fontSize: '10px',
    backgroundColor: status === 'completed' ? 'var(--success)' : 'var(--warning)',
    color: status === 'completed' ? '#fff' : '#000',
    borderRadius: '3px'
  }}>
    {status === 'completed' ? completedLabel : pendingLabel}
  </span>
);

// ---- Shared hooks ----

/** Hook for warehouse loading */
export const useWarehouses = (locationId) => {
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  useEffect(() => {
    const loadWarehouses = async () => {
      if (!locationId) return;
      try {
        const response = await api.get('/warehouses');
        const data = response.data;
        if (data.success) {
        const locationWarehouses = data.data.filter(w => w.location_id === locationId);
        setWarehouses(locationWarehouses);
        // Uzgodnij wybrany magazyn z nowa lokalizacja: jesli obecnie wybrany
        // nie nalezy do tej lokalizacji (np. po zmianie lokalizacji), zresetuj
        // do pierwszego dostepnego (lub null gdy brak magazynow).
        setSelectedWarehouse(prev => {
          if (prev && locationWarehouses.some(w => w.id === prev)) {
            return prev;
          }
          return locationWarehouses.length > 0 ? locationWarehouses[0].id : null;
        });
        }
      } catch (err) {
        console.error('Błąd ładowania magazynów:', err);
      }
    };
    loadWarehouses();
  }, [locationId]);

  return { warehouses, selectedWarehouse, setSelectedWarehouse };
};

/** Hook for inventory-based product loading (for issues - products from stock) */
export const useInventoryProducts = (selectedWarehouse) => {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const loadProducts = useCallback(async (search = '') => {
    if (!selectedWarehouse) {
      setProducts([]);
      return;
    }
    setLoadingProducts(true);
    try {
      const result = await warehouseService.getInventory({
        warehouse_id: selectedWarehouse,
        search: search,
        limit: 50,
        available_only: true
      });
      if (result.success && result.data?.products) {
        const availableProducts = result.data.products.filter(p =>
          (p.stock_quantity || p.quantity || 0) > 0
        );
        setProducts(availableProducts);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Błąd ładowania produktów:', err);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [selectedWarehouse]);

  return { products, loadingProducts, loadProducts, setProducts };
};

/** Hook for product search with debounce */
export const useProductSearch = (showProductModal, searchTerm, loadProducts, deps = []) => {
  // Pojedyncze zrodlo ladowania: reaguje na otwarcie modala, zmiane frazy
  // oraz zmiane zaleznosci (np. wybrany magazyn). Wczesniej dwa osobne
  // useEffect powodowaly podwojne ladowanie przy otwarciu modala.
  useEffect(() => {
    if (!showProductModal) return;
    if (searchTerm.length >= 2) {
      const timeoutId = setTimeout(() => {
        loadProducts(searchTerm);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else if (searchTerm.length === 0) {
      // pusta fraza (w tym otwarcie modala) -> jednorazowe pelne ladowanie
      loadProducts('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, showProductModal, loadProducts, ...deps]);
};

/** Hook for managing selected products list.
 *
 * @param {Function} setError - error setter
 * @param {Object} [options]
 * @param {boolean} [options.enforceMaxQuantity=true] - czy egzekwować limit max (stock_quantity).
 *   Sensowne dla WZ/RW (wydanie - nie wiecej niz jest na stanie). Wylacz dla PW
 *   (przyjecie wewnetrzne) gdzie nie ma logicznego limitu.
 */
export const useSelectedProducts = (setError, options = {}) => {
  const { enforceMaxQuantity = true } = options;
  const [selectedProducts, setSelectedProducts] = useState([]);

  const addProduct = useCallback((product, extraFields = {}) => {
    setSelectedProducts(prev => {
      const existing = prev.find(p => p.id === product.id);
      if (existing) {
        setError('Produkt już został dodany do listy');
        return prev;
      }
      const availableQty = product.stock_quantity || product.quantity || 0;
      return [...prev, {
        ...product,
        quantity: 1,
        reason: '',
        maxQuantity: enforceMaxQuantity ? availableQty : null,
        ...extraFields
      }];
    });
  }, [setError, enforceMaxQuantity]);

  const updateQuantity = useCallback((productId, quantity) => {
    const qty = parseInt(quantity) || 0;
    if (qty <= 0) {
      setSelectedProducts(prev => prev.filter(p => p.id !== productId));
      return;
    }
    setSelectedProducts(prev =>
      prev.map(p => {
        if (p.id === productId) {
          if (enforceMaxQuantity && p.maxQuantity != null && qty > p.maxQuantity) {
            setError(`Maksymalna dostępna ilość dla ${p.name}: ${p.maxQuantity}`);
            return p;
          }
          return { ...p, quantity: qty };
        }
        return p;
      })
    );
  }, [setError, enforceMaxQuantity]);

  const updateReason = useCallback((productId, reason) => {
    setSelectedProducts(prev =>
      prev.map(p =>
        p.id === productId
          ? { ...p, reason }
          : p
      )
    );
  }, []);

  const removeProduct = useCallback((productId) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  }, []);

  const clearProducts = useCallback(() => {
    setSelectedProducts([]);
  }, []);

  return {
    selectedProducts,
    setSelectedProducts,
    addProduct,
    updateQuantity,
    updateReason,
    removeProduct,
    clearProducts
  };
};

export default {
  AlertBanner,
  TabSwitcher,
  ProductSelectionModal,
  HistoryPanel,
  SelectedProductsTable,
  DetailsModal,
  StatusBadge,
  useWarehouses,
  useInventoryProducts,
  useProductSearch,
  useSelectedProducts,
};
